import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './WhyChooseUs.css';

const stats = [
  { key: 'years',    value: 10,  suffix: '+' },
  { key: 'cases',    value: 150, suffix: '+' },
  { key: 'response', value: 24,  suffix: 'h' },
];

function WhyChooseUs() {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const rafRef = useRef(0);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState(() => stats.map(() => 0));

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries.some((e) => e.isIntersecting));
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setCounts(stats.map(() => 0));
      return undefined;
    }

    if (prefersReducedMotion) {
      setCounts(stats.map((s) => s.value));
      return undefined;
    }

    const durationMs = 900;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setCounts(stats.map((s) => Math.round(s.value * eased)));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isVisible, prefersReducedMotion]);

  return (
    <section
      ref={sectionRef}
      className="py-5 py-lg-6 why-choose-us border-top border-bottom bg-light"
      id="why-choose-us"
    >
      <div className="container">
        <div className="row justify-content-center mb-4 mb-lg-5">
          <div className="col-lg-8 text-center">
            <h2 className="h3 fw-semibold text-navy mb-2">{t('whyChooseUs.heading')}</h2>
            <p className="text-muted mb-0">{t('whyChooseUs.subheading')}</p>
          </div>
        </div>

        <div className="row g-3 g-md-4 justify-content-center">
          {stats.map((stat, idx) => (
            <div className="col-12 col-sm-6 col-lg-4" key={stat.key}>
              <div className="why-stat h-100 text-center p-3 p-md-4">
                  <div className="why-stat-number text-navy fw-semibold">
                    <span aria-label={`${stat.value}${stat.suffix}`}>{counts[idx]}</span>
                    <span className="why-stat-suffix">{stat.suffix}</span>
                  </div>
                  <div className="why-stat-label text-muted">{t(`whyChooseUs.stats.${stat.key}`)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhyChooseUs;
